/**
 * Header Encryption Utility
 * Matches iOS implementation for encoding header values
 */

/**
 * Generate a random string of specified length
 * @param length - Length of the random string
 * @returns Random alphanumeric string
 */
function generateRandomString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

/**
 * Encode header value with random padding (iOS style)
 * Format: randomString(5) + value + randomString(5)
 * Then base64 encode the result
 * @param value - The value to encode
 * @returns Base64 encoded string with random padding
 */
export function encodeHeaderValue(value: string): string {
  const randomPrefix = generateRandomString(5);
  const randomSuffix = generateRandomString(5);
  const paddedValue = randomPrefix + value.trim() + randomSuffix;
  
  // Base64 encode
  return btoa(paddedValue);
}

/**
 * Decode header value (removes random padding and base64 decodes)
 * @param encodedValue - The encoded value
 * @returns Decoded string without padding
 */
export function decodeHeaderValue(encodedValue: string): string {
  try {
    const decoded = atob(encodedValue);
    // Remove first 5 and last 5 characters (random padding)
    return decoded.substring(5, decoded.length - 5);
  } catch (error) {
    console.error('Failed to decode header value:', error);
    return '';
  }
}

/**
 * Get user's current location
 * @returns Promise with latitude and longitude
 */
export async function getUserLocation(): Promise<{ latitude: string; longitude: string }> {
  return new Promise((resolve) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          });
        },
        (error) => {
          console.warn('Could not get user location:', error);
          // Return default values if location access is denied
          resolve({
            latitude: '0.0',
            longitude: '0.0'
          });
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    } else {
      // Geolocation not supported
      resolve({
        latitude: '0.0',
        longitude: '0.0'
      });
    }
  });
}

/**
 * Get user's IP address
 * @returns Promise with IP address
 */
export async function getUserIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || '0.0.0.0';
  } catch (error) {
    console.warn('Could not get user IP:', error);
    return '0.0.0.0';
  }
}

/**
 * Get device name/browser info
 * @returns Device/browser name
 */
export function getDeviceName(): string {
  const userAgent = navigator.userAgent || '';

  // Detect OS
  let os = 'Web';
  if (/windows nt 10/i.test(userAgent)) os = 'Windows';
  else if (/windows nt 6.3/i.test(userAgent)) os = 'Windows 8.1';
  else if (/windows nt 6.1/i.test(userAgent)) os = 'Windows 7';
  else if (/macintosh|mac os x/i.test(userAgent)) os = 'macOS';
  else if (/iphone|ipad|ipod/i.test(userAgent)) os = 'iOS';
  else if (/android/i.test(userAgent)) os = 'Android';
  else if (/linux|x11/i.test(userAgent)) os = 'Linux';

  // Detect Browser & Version
  let browser = 'Web Browser';
  const edgMatch = userAgent.match(/edg\/([\d.]+)/i);
  const chromeMatch = userAgent.match(/chrome\/([\d.]+)/i);
  const firefoxMatch = userAgent.match(/firefox\/([\d.]+)/i);
  const safariMatch = userAgent.match(/version\/([\d.]+).*safari/i);

  if (edgMatch) browser = `Edge ${edgMatch[1].split('.')[0]}`;
  else if (chromeMatch) browser = `Chrome ${chromeMatch[1].split('.')[0]}`;
  else if (firefoxMatch) browser = `Firefox ${firefoxMatch[1].split('.')[0]}`;
  else if (safariMatch) browser = `Safari ${safariMatch[1].split('.')[0]}`;
  else if (/safari/i.test(userAgent)) browser = 'Safari';

  return `${browser} on ${os}`;
}

/**
 * Get app version from package.json or environment
 * @returns App version
 */
export function getAppVersion(): string {
  return process.env.REACT_APP_VERSION || '1.0.0';
}

/**
 * Get formatted address from geolocation (reverse geocoding)
 * Uses free nominatim service as fallback if Google Maps is not available
 * @param latitude - Latitude
 * @param longitude - Longitude
 * @returns Promise with formatted address
 */
export async function getAddressFromCoordinates(latitude: string, longitude: string): Promise<string> {
  try {
    // Skip if coordinates are default/invalid
    if (latitude === '0.0' || longitude === '0.0' || !latitude || !longitude) {
      return 'Unknown Location';
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    // Try Google Maps first if API key is available
    if (apiKey) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
          { signal: controller.signal }
        );
        
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();

          if (data.status === 'OK' && data.results && data.results.length > 0) {
            const address = data.results[0].formatted_address;
            return address || 'Unknown Location';
          }
        }
      } catch (googleError) {
        console.warn('Google Maps geocoding failed, trying fallback:', googleError);
      }
    }

    // Fallback to free OpenStreetMap Nominatim service
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
        { 
          signal: controller.signal,
          headers: {
            'User-Agent': 'TechnBuddy-Web-App'
          }
        }
      );
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.display_name) {
          return data.display_name;
        }
      }
    } catch (nominatimError) {
      console.warn('Nominatim geocoding failed:', nominatimError);
    }

    // Final fallback: return coordinates as string
    return `Location: ${parseFloat(latitude).toFixed(4)}, ${parseFloat(longitude).toFixed(4)}`;
  } catch (error: any) {
    console.warn('Could not get address from coordinates:', error.message || error);
    return 'Unknown Location';
  }
}

/**
 * Generate all encoded headers for API requests
 * @returns Promise with all encoded headers
 */
export async function generateEncodedHeaders(): Promise<{
  latitude: string;
  longitude: string;
  devicename: string;
  ip: string;
  version: string;
  address: string;
}> {
  // Get location first
  const location = await getUserLocation();
  
  // Get device info (synchronous)
  const deviceName = getDeviceName();
  const version = getAppVersion();

  // Get IP and address in parallel, with individual error handling
  const [ip, address] = await Promise.allSettled([
    getUserIP(),
    getAddressFromCoordinates(location.latitude, location.longitude)
  ]).then(results => [
    results[0].status === 'fulfilled' ? results[0].value : '0.0.0.0',
    results[1].status === 'fulfilled' ? results[1].value : 'Unknown Location'
  ]);

  // Encode all values
  return {
    latitude: encodeHeaderValue(location.latitude),
    longitude: encodeHeaderValue(location.longitude),
    devicename: encodeHeaderValue(deviceName),
    ip: encodeHeaderValue(ip),
    version: encodeHeaderValue(version),
    address: encodeHeaderValue(address)
  };
}

/**
 * Cache for encoded headers to avoid repeated API calls
 */
let cachedHeaders: {
  headers: {
    latitude: string;
    longitude: string;
    devicename: string;
    ip: string;
    version: string;
    address: string;
  } | null;
  timestamp: number;
} = {
  headers: null,
  timestamp: 0
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get encoded headers with caching
 * @param forceRefresh - Force refresh the cache
 * @returns Promise with encoded headers
 */
export async function getCachedEncodedHeaders(forceRefresh: boolean = false): Promise<{
  latitude: string;
  longitude: string;
  devicename: string;
  ip: string;
  version: string;
  address: string;
}> {
  const now = Date.now();
  
  // Return cached headers if still valid
  if (!forceRefresh && cachedHeaders.headers && (now - cachedHeaders.timestamp) < CACHE_DURATION) {
    return cachedHeaders.headers;
  }

  // Generate new headers
  const headers = await generateEncodedHeaders();
  
  // Update cache
  cachedHeaders = {
    headers,
    timestamp: now
  };

  return headers;
}
